import toast from 'react-hot-toast'

const AUTH_MESSAGES = {
  'invalid-credential': 'Email or password is incorrect.',
  'wrong-password': 'Email or password is incorrect.',
  'user-not-found': 'Email or password is incorrect.',
  'email-already-in-use': 'An account with this email already exists. Try signing in.',
  'invalid-email': 'Enter a valid email address.',
  'weak-password': 'Password must be at least 6 characters.',
  'too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'requires-recent-login': 'For your security, sign out, sign back in, and try again.',
  'missing-email': 'Enter your email address.',
  'network-request-failed': 'Connection problem. Check your internet and try again.'
}

const SILENT_AUTH = new Set([
  'popup-closed-by-user',
  'cancelled-popup-request',
  'popup-closed',
  'user-cancelled'
])

const TECHNICAL =
  /firebase|firestore|openai|replicate|\bstripe\b|gelato|serpapi|okta|dall-?e|\bgpt-\d|printful|midjourney|\bsdxl\b|api returned|\bhttp\s*\d{3}\b|\bstatus\s*\d{3}\b|\b[45]\d{2}\b|stack trace|typeerror|referenceerror|syntaxerror|firebaseerror|auth\/|econn|enotfound|etimedout|npm run|\.env\b|localhost|127\.0\.0\.1|secret key|api key|webhook|internal server|permission-denied|productuid|failed to fetch|networkerror|network request failed|cors\b|\.jsx?:\d+|at \S+\s+\(/i

const GENERIC = 'Something went wrong. Please try again.'

// Names the customer's own words, which may contain anything the TECHNICAL filter hides.
const BRAND_BLOCKED =
  /^We can't use '.{1,80}' because it looks like an existing brand or character\. Please describe an original design\.$/

function normalizeAuthCode(error) {
  const raw = String(error?.code || '')
  if (raw) return raw.replace(/^auth\//, '')
  const msg = String(error?.message || '')
  const match = msg.match(/auth\/([a-z0-9-]+)/i)
  if (match) return match[1].toLowerCase()
  if (/popup has been closed|popup closed|cancelled popup|canceled popup/i.test(msg)) {
    return 'popup-closed-by-user'
  }
  return ''
}

export function isTechnicalMessage(text) {
  const value = String(text || '').trim()
  if (!value) return true
  if (BRAND_BLOCKED.test(value)) return false
  if (TECHNICAL.test(value)) return true
  if (/^[A-Z0-9_]{6,}$/.test(value)) return true
  if (value.length > 280) return true
  return false
}

/** Firebase auth toast text. Null means show nothing (closed or cancelled popup). */
export function authErrorMessage(error) {
  const code = normalizeAuthCode(error)
  if (SILENT_AUTH.has(code)) return null
  if (AUTH_MESSAGES[code]) return AUTH_MESSAGES[code]
  return GENERIC
}

function technicalDetail(error, raw) {
  const fromServer = typeof error === 'object' && error?.detail ? String(error.detail) : ''
  const source = (fromServer || raw || '').replace(/\s+/g, ' ').trim()
  return source.slice(0, 300)
}

/**
 * Customer-safe text. Existing friendly sentences are kept.
 * Admins may also see one Technical details line.
 */
export function customerMessage(error, fallback, { isAdmin = false } = {}) {
  const raw = typeof error === 'string' ? error : String(error?.message || '')
  const friendlySource = raw && !isTechnicalMessage(raw) ? raw.trim() : ''
  const friendly = friendlySource || fallback || GENERIC
  if (isAdmin !== true) return friendly
  const detail = technicalDetail(error, raw)
  if (!detail || detail === friendly) return friendly
  return `${friendly}\nTechnical details: ${detail}`
}

export function toastCustomerError(error, fallback, options = {}) {
  const text = customerMessage(error, fallback, options)
  if (!text) return
  toast.error(text, {
    id: options.id,
    duration: options.duration,
    style: text.includes('\n') ? { whiteSpace: 'pre-line' } : undefined
  })
}

export function toastAuthError(error) {
  const text = authErrorMessage(error)
  if (!text) return
  toast.error(text)
}

export function errorFromApi(data = {}, status, fallback) {
  const messageField = typeof data.message === 'string' ? data.message : ''
  const errorField = typeof data.error === 'string' ? data.error : ''
  const err = new Error(messageField || errorField || fallback || GENERIC)
  err.status = status
  err.code = data.code || errorField || null
  if (data.detail) err.detail = String(data.detail)
  return err
}
